// The installed app's icon (FE-09): the X avatar's keke on its road, dressed for the season (the
// everyday jollof between seasons; the other flavours are drawn and ready, not offered yet). A browser re-reads the manifest whenever the installed app opens, so
// pointing the manifest link at another icon set is what changes the icon on the home screen or the
// desktop (Chrome and Edge do; iOS keeps the icon it took when the app was added). scripts/icons.mjs
// renders every one of these into public/icons and public/manifests.

/** The everyday collection; jollof is the X avatar and the default. */
export const FLAVOURS = ['jollof', 'zobo', 'lagoon', 'dodo', 'ankara', 'smoke'] as const;
/** The seasons, each worn only inside its dates (Lagos time). */
export const SEASONS = ['detty', 'independence', 'harmattan', 'rainy'] as const;
export type Flavour = (typeof FLAVOURS)[number];
export type Season = (typeof SEASONS)[number];
export type IconId = Flavour | Season;
/** 'season': the season's icon while one runs, the default flavour between them. */
export type IconChoice = 'season' | Flavour;

type Day = readonly [month: number, day: number];
/** First match wins, so the short festivals sit above the long weather seasons they fall inside. */
const CALENDAR: ReadonlyArray<readonly [Season, Day, Day]> = [
  ['detty', [12, 15], [1, 2]],
  ['independence', [9, 28], [10, 3]],
  ['harmattan', [11, 15], [2, 28]],
  ['rainy', [4, 15], [7, 31]],
];

const at = ([m, d]: Day): number => m * 100 + d;

/** The season running on this date in Lagos (UTC+1, no daylight saving), or null between seasons. */
export function seasonOn(now: Date): Season | null {
  const lagos = new Date(now.getTime() + 3_600_000);
  const today = (lagos.getUTCMonth() + 1) * 100 + lagos.getUTCDate();
  for (const [season, from, to] of CALENDAR) {
    const a = at(from);
    const b = at(to);
    // a season that runs over the new year wraps
    if (a <= b ? today >= a && today <= b : today >= a || today <= b) return season;
  }
  return null;
}

export function iconFor(choice: IconChoice, now: Date): IconId {
  return choice === 'season' ? (seasonOn(now) ?? 'jollof') : choice;
}

export const isIconChoice = (v: unknown): v is IconChoice =>
  v === 'season' || (FLAVOURS as readonly unknown[]).includes(v);

/** Point the page's manifest and home-screen icon at the icon set for this choice today. */
export function applyAppIcon(choice: IconChoice, now = new Date()): void {
  if (typeof document === 'undefined') return;
  const id = iconFor(choice, now);
  const link = (rel: string, href: string) => {
    let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.rel = rel;
      document.head.append(el);
    }
    if (el.getAttribute('href') !== href) el.href = href;
  };
  link('manifest', `/manifests/${id}.webmanifest`);
  link('apple-touch-icon', `/icons/${id}-180.png`);
}
